<template>
  <div class="open-popup" v-if="show">
    <div class="open-popup__mask" @click="onClose"></div>

    <div class="open-popup__panel">
      <!-- 顶部标题条 + 装饰图 -->
      <div class="open-popup__header">
        <div class="open-popup__header-main">
          <span class="open-popup__title">{{ cardTitle }}</span>
          <span v-if="isBasicTab" class="open-popup__welfare">限时福利</span>
        </div>
        <div class="open-popup__decor">
          <img
            class="open-popup__decor-close"
            referrerpolicy="no-referrer"
            src="https://iqh-zt.oss-cn-beijing.aliyuncs.com/ark-student-wap/member_new/open-popup__close.png"
            alt=""
            @click.stop="onClose"
          />
        </div>
      </div>

      <!-- 白色内容区 -->
      <div class="open-popup__sheet">
        <div class="open-popup__body">
          <div
            v-if="showTrialDesc"
            class="open-popup__trial-desc"
            @click="onTrialClick"
          >
            <img
              src="https://iqh-zt.oss-cn-beijing.aliyuncs.com/ark-student-wap/member_new/open-popup__trial-desc.png"
            />
          </div>

          <h4 v-if="packageList.length" class="open-popup__section-title">选择套餐</h4>
          <!-- 套餐列表：来自父页 priceInfo（支持同档多套餐，如季卡/年卡） -->
          <div
            v-if="packageList.length"
            class="open-popup__package-list"
            :class="{ 'is-single': packageList.length === 1 }"
          >
            <div
              v-for="item in packageList"
              :key="item.id"
              class="open-popup__package"
              :class="{ 'is-active': item.id === selectedPackageId }"
              @click="selectPackage(item)"
            >
              <span v-if="item.recommend" class="open-popup__package-badge"></span>
              <div class="open-popup__package-main">
                <span class="open-popup__package-name">{{ item.name }}</span>
                <div class="open-popup__package-price">
                  <span class="open-popup__package-symbol">¥</span>
                  <span class="open-popup__package-amount">{{ formatPrice(item.price) }}</span>
                </div>
                <span v-if="item.originalPrice" class="open-popup__package-origin"
                  >¥{{ item.originalPrice }}</span
                >
                <div
                  v-if="formatCountdown(item.endTime)"
                  class="open-popup__package-countdown"
                >
                  活动倒计时：{{ formatCountdown(item.endTime) }}
                </div>
              </div>
              <div
                v-if="formatDailyPrice(item)"
                class="open-popup__package-daily"
              >
                {{ formatDailyPrice(item) }}
              </div>
            </div>
          </div>

          <!-- 权益列表：按当前会员等级过滤 -->
          <div v-if="filteredBenefitNames.length" class="open-popup__rights">
            <div class="open-popup__rights-title">开通后可享受以下权益</div>
            <ul class="open-popup__rights-list">
              <li
                v-for="(name, index) in filteredBenefitNames"
                :key="`${name}-${index}`"
                class="open-popup__rights-item"
              >
                <i class="open-popup__rights-dot"></i>
                <span>{{ name }}</span>
              </li>
            </ul>
          </div>
        </div>

        <div class="open-popup__footer">
          <button
            class="open-popup__pay"
            :class="{ 'is-disabled': payDisabled }"
            type="button"
            :disabled="payDisabled"
            @click="onPay"
          >
            {{ payBtnText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/** 权益档位（模块级函数，避免 computed 里 this.方法名写错） */
function resolveBenefitTier(levels) {
  const has20 = levels.includes(20);
  const has25 = levels.includes(25);
  const has30 = levels.includes(30);
  if (has20 && has25 && has30) return 'all';
  if (has20 && !has25 && !has30) return 'basic';
  if (has30 && !has25) return 'svip';
  if (has25 && !has30) return 'vip';
  if (has25 && has30 && !has20) return 'vip';
  return 'all';
}

export default {
  name: 'OpenMemberPopup',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // 当前选中的会员等级：20 / 25 / 30
    activeTab: {
      type: Number,
      default: 20,
    },
    // 与父页 tab 文案一致，如「基础卡」
    cardTitle: {
      type: String,
      default: '',
    },
    // 权益列表，项含 title、memberLevels
    benefitList: {
      type: Array,
      default: () => [],
    },
    // 当前 tab 套餐列表（含 priceInfo 字段：price / originalPrice / endTime）
    packages: {
      type: Array,
      default: () => [],
    },
    // 用户已购有效档位：0 / 20 / 25 / 30
    memberTierLevel: {
      type: Number,
      default: 0,
    },
    // 父页是否可领试用：未买过会员 + 后台有试用套餐
    showTrial: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPackageId: '',
      now: Date.now(),
      timer: null,
      trialImgError: false,
    };
  },
  computed: {
    // 仅基础会员 tab 展示限时福利 / 试用说明
    isBasicTab() {
      return Number(this.activeTab) === 20;
    },
    // 基础卡 + 父页允许试用
    showTrialDesc() {
      return this.isBasicTab && this.showTrial;
    },
    // 当前 tab 高于已购档 → 升级
    isUpgrade() {
      const tier = Number(this.memberTierLevel) || 0;
      return tier > 0 && tier < Number(this.activeTab);
    },
    // 已拥有当前档（含刚领的试用覆盖基础卡）→ 置灰不可买；切到更高档仍可买/升级
    payDisabled() {
      return Number(this.memberTierLevel) >= Number(this.activeTab);
    },
    payBtnText() {
      if (this.isUpgrade) return '升级';
      return `立即支付¥${this.displayPrice}`;
    },
    packageList() {
      return this.packages || [];
    },
    /**
     * 开通弹窗权益过滤（高档继承低档）：
     * - basic / all（如 [20]、[20,25,30]）→ 20 / 25 / 30 都展示
     * - vip（如 [25]、[20,25]、[25,30]）→ 仅 25 / 30
     * - svip（如 [30]、[20,30]）→ 仅 30
     */
    filteredBenefitNames() {
      const tab = Number(this.activeTab);
      return (this.benefitList || [])
        .filter((item) => {
          const levels = (item.memberLevels || []).map(Number);
          if (!levels.length) return false;
          const tier = resolveBenefitTier(levels);
          if (tier === 'basic' || tier === 'all') return [20, 25, 30].includes(tab);
          if (tier === 'vip') return [25, 30].includes(tab);
          if (tier === 'svip') return tab === 30;
          return false;
        })
        .map((item) => item.title)
        .filter(Boolean);
    },
    selectedPackage() {
      return (
        this.packageList.find(item => item.id === this.selectedPackageId)
        || this.packageList.find(item => item.recommend)
        || this.packageList[0]
        || {}
      );
    },
    displayPrice() {
      return this.formatPrice(this.selectedPackage.price);
    },
    hasCountdown() {
      return this.packageList.some(item => item.endTime);
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(flag) {
        document.body.style.overflow = flag ? 'hidden' : '';
        if (flag) {
          this.syncSelectedPackage();
          this.startCountdown();
        } else {
          this.clearCountdown();
        }
      },
    },
    packages: {
      deep: true,
      handler() {
        this.syncSelectedPackage();
        if (this.show) this.startCountdown();
      },
    },
    activeTab() {
      this.syncSelectedPackage();
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    this.clearCountdown();
  },
  methods: {
    syncSelectedPackage() {
      const list = this.packageList;
      if (!list.length) {
        this.selectedPackageId = '';
        return;
      }
      const stillExists = list.some(item => item.id === this.selectedPackageId);
      if (stillExists) return;
      const recommend = list.find(item => item.recommend) || list[list.length - 1];
      this.selectedPackageId = recommend.id;
    },
    selectPackage(item) {
      if (!item || !item.id) return;
      this.selectedPackageId = item.id;
    },
    formatPrice(price) {
      if (price === '' || price === null || price === undefined) return '--';
      return price;
    },
    formatCountdown(endTime) {
      if (!endTime) return '';
      const end = new Date(endTime).getTime();
      if (Number.isNaN(end)) return '';
      let diff = Math.max(0, end - this.now);
      const day = Math.floor(diff / 86400000);
      diff %= 86400000;
      const hour = Math.floor(diff / 3600000);
      diff %= 3600000;
      const minute = Math.floor(diff / 60000);
      diff %= 60000;
      const second = Math.floor(diff / 1000);
      const pad = n => String(n).padStart(2, '0');
      return `${day}天${pad(hour)}:${pad(minute)}:${pad(second)}`;
    },
    formatDailyPrice(item = {}) {
      const num = Number(item.price);
      if (!num || Number.isNaN(num)) return '';
      const dayNum = this.getPackageDays(item);
      return `低至${(num / dayNum).toFixed(2)}/天`;
    },
    // 年卡 /365，季卡 /90
    getPackageDays(item = {}) {
      const type = Number(item.memberPackageType);
      if (type === 2) return 365;
      if (type === 1) return 90;
      const name = item.name || '';
      if (/年卡/.test(name)) return 365;
      if (/季卡/.test(name)) return 90;
      const days = Number(item.days);
      return days > 0 ? days : 365;
    },
    // 与主站权益角标档位一致
    getBenefitTier(levels) {
      const has20 = levels.includes(20);
      const has25 = levels.includes(25);
      const has30 = levels.includes(30);
      if (has20 && has25 && has30) return 'all';
      if (has20 && !has25 && !has30) return 'basic';
      if (has30 && !has25) return 'svip';
      if (has25 && !has30) return 'vip';
      if (has25 && has30 && !has20) return 'vip';
      return 'all';
    },
    startCountdown() {
      this.clearCountdown();
      this.now = Date.now();
      if (!this.hasCountdown) return;
      this.timer = setInterval(() => {
        this.now = Date.now();
      }, 1000);
    },
    clearCountdown() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    onClose() {
      this.$emit('close');
    },
    onTrialClick() {
      this.$emit('trial');
    },
    onPay() {
      if (this.payDisabled) return;
      if (this.isUpgrade) {
        this.$emit('upgrade');
        return;
      }
      this.$emit('pay', this.selectedPackage);
    },
  },
};
</script>

<style lang="scss" scoped>
.open-popup {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 120;

  &__mask {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.7);
  }

  &__panel {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &__header {
    position: relative;
    z-index: 1;
    height: 205px;
    padding: 78px 54px 0;
    background: url(https://iqh-zt.oss-cn-beijing.aliyuncs.com/ark-student-wap/member_new/open-popup__header.png);
    background-size: 100% 100%;
  }

  &__header-main {
    display: flex;
    align-items: center;
  }

  &__title {
    color: #60371c;
    font-weight: 600;
    font-size: 36px;
    line-height: 51px;
  }

  &__welfare {
    display: inline-block;
    width: 149px;
    height: 47px;
    margin-left: 18px;
    color: #60371c;
    font-weight: 500;
    font-size: 29px;
    line-height: 47px;
    text-align: center;
    background: linear-gradient(180deg, #fff4c9 0%, #ffe698 100%);
    border: 2px solid #fff;
    border-radius: 24px;
  }

  &__decor {
    position: absolute;
    top: -22px;
    right: 0;
    width: 230px;
    height: 205px;
  }

  &__decor-close {
    position: absolute;
    top: 69px;
    right: 0;
    width: 69px;
    height: 69px;
  }

  &__sheet {
    position: relative;
    z-index: 2;
    margin-top: -54px;
    background-color: #fff;
    border-radius: 36px 36px 0 0;
  }

  &__body {
    max-height: 70vh;
    padding: 36px 36px 20px;
    overflow-y: auto;
  }

  &__trial-desc {
    position: relative;
    width: 100%;
    min-height: 80px;
    margin: 0 auto 20px;
    overflow: hidden;
    background: linear-gradient(90deg, #fff3e6 0%, #ffe7d1 100%);
    border-radius: 12px;
    cursor: pointer;

    img {
      display: block;
      width: 100%;
      height: auto;
      min-height: 80px;
      object-fit: contain;
    }
  }

  &__trial-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 80px;
    color: #ff7f41;
    font-weight: 500;
    font-size: 28px;
  }

  &__section-title {
    margin: 0;
    color: #333;
    font-weight: 500;
    font-size: 29px;
    line-height: 40px;
  }

  &__package-list {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    margin-top: 22px;

    // 基础会员仅一种套餐时通栏展示
    &.is-single {
      .open-popup__package {
        max-width: 100%;
      }
    }
  }

  &__package {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    background-color: #fff;
    border: 2px solid transparent;
    border-radius: 18px;

    & + & {
      margin-left: 18px;
    }

    &.is-active {
      background-color: #fff5dd;
      border-color: #eccd60;

      .open-popup__package-name {
        color: #60371c;
      }

      .open-popup__package-price {
        color: #ff3938;
      }

      .open-popup__package-countdown {
        color: #60371c;
        background-color: #fdee92;
      }

      .open-popup__package-daily {
        color: #60371c;
        background-color: #ffe6a1;
      }
    }
  }

  &__package-badge {
    position: absolute;
    top: -20px;
    right: 0;
    z-index: 1;
    width: 105px;
    height: 47px;
    background: url(https://iqh-zt.oss-cn-beijing.aliyuncs.com/ark-student-wap/member_new/open-popup__recommend.png)
      no-repeat;
    background-size: 100% 100%;
  }

  &__package-main {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    padding: 34px 10px 33px;
  }

  &__package-name {
    color: #333;
    font-weight: 600;
    font-size: 29px;
    line-height: 40px;
  }

  &__package-price {
    display: flex;
    align-items: flex-end;
    margin-top: 11px;
    color: #333;
    font-weight: 600;
    line-height: 1;
  }

  &__package-symbol {
    margin-right: 4px;
    font-size: 29px;
    line-height: 40px;
  }

  &__package-amount {
    font-size: 47px;
    line-height: 47px;
  }

  &__package-origin {
    margin-top: 4px;
    color: #999;
    font-size: 25px;
    line-height: 36px;
    text-decoration: line-through;
  }

  &__package-countdown {
    margin-top: 18px;
    padding: 4px 12px;
    color: #999;
    font-size: 20px;
    line-height: 31px;
    white-space: nowrap;
    background-color: #f2f2f2;
    border-radius: 22px;
  }

  &__package-daily {
    flex-shrink: 0;
    margin-top: auto;
    padding: 14px 0;
    color: #999;
    font-size: 25px;
    line-height: 36px;
    text-align: center;
    background-color: #f2f2f2;
    border-radius: 0 0 14px 14px;
  }

  &__rights {
    margin-top: 22px;
    padding: 29px 36px 36px;
    background-color: #f7f8fc;
    border-radius: 18px;
  }

  &__rights-title {
    color: #333;
    font-weight: 500;
    font-size: 29px;
    line-height: 40px;
  }

  &__rights-list {
    display: flex;
    flex-wrap: wrap;
    margin: 22px 0 0;
    padding: 0;
    list-style: none;
  }

  &__rights-item {
    display: flex;
    align-items: center;
    width: 33.33%;
    margin-top: 22px;
    color: #333;
    font-size: 25px;
    line-height: 36px;

    &:nth-child(-n + 3) {
      margin-top: 0;
    }
  }

  &__rights-dot {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-right: 10px;
    background-color: #d8d8d8;
    border-radius: 50%;
  }

  &__footer {
    padding: 22px 36px calc(14px + env(safe-area-inset-bottom));
    background-color: #fff;
    border-radius: 22px 22px 0 0;
    box-shadow: 0 -4px 14px 0 rgba(0, 0, 0, 0.05);
  }

  &__pay {
    display: block;
    width: 100%;
    padding: 20px 0;
    color: #fff;
    font-weight: 500;
    font-size: 29px;
    line-height: 40px;
    text-align: center;
    background-color: #ff7f41;
    border: none;
    border-radius: 49px;

    &.is-disabled,
    &:disabled {
      color: #fff;
      background-color: #ccc;
      pointer-events: none;
    }
  }
}
</style>
